import { useCallback, useEffect, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import { Edit as EditIcon } from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";
import { PageContainer } from "@/features/ui";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { apiClient, getErrorMessage } from "@/features/shared";
import { SearchTextField, useSnackbar } from "@/features/ui";
import { useSearchFilter, useServerPagination } from "@/hooks";

export const Suppliers = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await apiClient.get("suppliers", {
        params: {
          ...paginationParams,
          ...(keyword && { name: keyword }),
          withSupplierCategory: true,
        },
      });
      setSuppliers(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat pemasok"));
    } finally {
      setLoading(false);
    }
  }, [paginationParams, keyword, setRowCount, showSnackbar]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const toolbarContent = hasPermission(PermissionEnum.CREATE_SUPPLIERS) && (
    <Button variant="contained" component={Link} to="create">
      Tambah
    </Button>
  );

  return (
    <PageContainer
      maxWidth="xl"
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={3}>
        <Grid container sx={{ justifyContent: "flex-end" }}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari nama pemasok"
              {...searchProps}
            />
          </Grid>
        </Grid>
        <DataGrid
          rows={suppliers}
          loading={loading}
          getRowHeight={() => "auto"}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 1.5,
              sortable: false,
            },
            {
              field: "code",
              headerName: "Kode",
              flex: 1,
              sortable: false,
            },
            {
              field: "supplierCategory",
              headerName: "Kategori",
              flex: 1.5,
              sortable: false,
              valueGetter: (supplierCategory) => supplierCategory.name,
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: (params) => {
                const actions = [];
                if (hasPermission(PermissionEnum.UPDATE_SUPPLIERS)) {
                  actions.push(
                    <IconButton
                      key="edit"
                      onClick={() => navigate(`${params.id}/edit`)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  );
                }
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
